import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BooleanOperator, InvoiceStateByAPI } from '@common/enums';
import { InvoiceOData, PayInvoicePayload } from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { SharedOdataService } from '@common/services';
import { PaymentsTag, PaymentsTagsMap } from '@common/services/smart-search';
import { FilterOptions } from '@common/types';
import { currencyInvert } from '@common/utils';
import {
  containsWrapper,
  filterByWords,
  getFilterByContains,
  getFilterByDate,
  getFilterByEq,
  getFilterByEqFromEnum
} from '@common/utils/functions/search';
import { SmartSearchModel, SmartSearchModelField } from 'mbs-ui-kit';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class InvoicesService extends SharedOdataService<InvoiceOData> {
  private readonly http = inject(HttpClient);

  readonly #amount = PaymentsTagsMap.get(PaymentsTag.Amount);
  readonly #date = PaymentsTagsMap.get(PaymentsTag.Date);
  readonly #expireDate = PaymentsTagsMap.get(PaymentsTag.ExpireDate);
  readonly #invoiceId = PaymentsTagsMap.get(PaymentsTag.InvoiceId);
  readonly #price = PaymentsTagsMap.get(PaymentsTag.Price);
  readonly #quantity = PaymentsTagsMap.get(PaymentsTag.Quantity);
  readonly #paymentState = PaymentsTagsMap.get(PaymentsTag.State);

  constructor() {
    super('Invoices');
  }

  getInvoices(): Observable<ODataPagedResult<InvoiceOData>> {
    const odataService = this.odataFactory.CreateService<InvoiceOData>('Invoices');

    return this.fetchData(odataService);
  }

  payInvoice(payload: PayInvoicePayload): Observable<any> {
    const endpoint = '/api/PayInvoice';

    return this.http.post(endpoint, payload);
  }

  updateFilter(searchObj: SmartSearchModel): void {
    const filter: string[] = [];

    if (searchObj[this.#paymentState.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#paymentState.tag] as SmartSearchModelField[],
        prop: this.#paymentState.prop,
        enumItems: InvoiceStateByAPI
      };

      filter.push(getFilterByEqFromEnum(options));
    }

    if (searchObj[this.#quantity.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#quantity.tag] as SmartSearchModelField[],
        prop: this.#quantity.prop
      };

      filter.push(getFilterByEq(options, false));
    }

    if (searchObj[this.#price.tag]) {
      const price = searchObj[this.#price.tag][0];
      price.value = currencyInvert(price.value);
      const options: FilterOptions = {
        model: searchObj[this.#price.tag] as SmartSearchModelField[],
        prop: this.#price.prop
      };

      filter.push(getFilterByEq(options, false));
    }

    if (searchObj[this.#amount.tag]) {
      const amount = searchObj[this.#amount.tag][0];
      amount.value = currencyInvert(amount.value);
      const options: FilterOptions = {
        model: searchObj[this.#amount.tag] as SmartSearchModelField[],
        prop: this.#amount.prop
      };

      filter.push(getFilterByEq(options, false));
    }

    if (searchObj[this.#date.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#date.tag] as SmartSearchModelField[],
        prop: this.#date.prop
      };

      filter.push(getFilterByDate(options));
    }

    if (searchObj[this.#expireDate.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#expireDate.tag] as SmartSearchModelField[],
        prop: this.#expireDate.prop
      };

      filter.push(getFilterByDate(options));
    }

    if (searchObj[this.#invoiceId.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#invoiceId.tag] as SmartSearchModelField[],
        prop: this.#invoiceId.prop
      };

      filter.push(getFilterByContains(options));
    }

    if (searchObj.words?.filter(Boolean)) {
      const term = filterByWords(searchObj);

      filter.push(containsWrapper(this.#invoiceId.prop, term));
    }

    this.filter = filter.length > 0 ? filter.join(` ${BooleanOperator.And} `) : '';
  }
}
